import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { myFetch } from '@/commons/fetch';

// Types
export interface Dashboard {
  id: string;
  category: string;
  owner: string;
  createdAt?: string;
  updatedAt?: string;
}

interface DashboardState {
  dashboard: Dashboard | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: DashboardState = {
  dashboard: null,
  isLoading: false,
  error: null,
};

// Async Thunks
export const fetchDashboard = createAsyncThunk(
  'dashboard/fetchDashboard',
  async (username: string, { rejectWithValue }) => {
    try {
      const response = await myFetch(`/${username}/dashboard`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue({
        message: error.message,
        status: error.status,
        statusText: error.statusText,
        body: error.body,
      });
    }
  }
);

export const updateDashboard = createAsyncThunk(
  'dashboard/updateDashboard',
  async (
    { username, category }: { username: string; category: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await myFetch(`/${username}/dashboard`, {
        method: 'PUT',
        body: JSON.stringify({ category }),
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue({
        message: error.message,
        status: error.status,
        statusText: error.statusText,
        body: error.body,
      });
    }
  }
);

// Slice
const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearDashboard: (state) => {
      state.dashboard = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch dashboard
    builder
      .addCase(fetchDashboard.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDashboard.fulfilled, (state, action) => {
        state.isLoading = false;
        state.dashboard = action.payload;
      })
      .addCase(fetchDashboard.rejected, (state, action) => {
        state.isLoading = false;
        const error = action.payload as any;
        state.error =
          error?.message || action.error.message || 'Failed to fetch dashboard';
      });

    // Update dashboard
    builder
      .addCase(updateDashboard.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateDashboard.fulfilled, (state, action) => {
        state.isLoading = false;
        state.dashboard = action.payload;
      })
      .addCase(updateDashboard.rejected, (state, action) => {
        state.isLoading = false;
        const error = action.payload as any;
        state.error =
          error?.message ||
          action.error.message ||
          'Failed to update dashboard';
      });
  },
});

export const { clearError, clearDashboard } = dashboardSlice.actions;
export default dashboardSlice.reducer;
